import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/lib/api';
import { usePlayer } from '@/contexts/PlayerContext';
import { usePlayerAuth } from '@/contexts/PlayerAuthContext';
import { PlayerShell } from '@/components/player/PlayerShell';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePageTitle } from '@/lib/usePageTitle';
import { useNoIndex } from '@/lib/useNoIndex';
import { useSEO } from '@/lib/useSEO';
import { CodeStep, AuthStep, EnterStep } from '@/components/player/GatekeeperSteps';

const ANIM_FADE_UP = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };
const TRANSITION_MAIN = { duration: 0.6, ease: 'easeOut' };

export default function GatekeeperPage() {
  const { code: urlCode } = useParams();
  const navigate = useNavigate();
  const { room, setRoom, savePlayerName } = usePlayer();
  const { player, isAuthenticated, signup, login, loading: authLoading } = usePlayerAuth();
  usePageTitle(room ? `Join ${room.tournament_name}` : 'Join a Pool');
  useNoIndex();  // private pool access page — never index

  // SEO/share-preview metadata for /joinpool/:code — most-shared link in the app.
  // Uses society+tournament context when available so iMessage/WhatsApp/Slack
  // previews read "Join [Society]'s [Tournament] pool" instead of the generic app title.
  const seo = useMemo(() => {
    if (!room) {
      return {
        title: 'Join a fantasy golf pool | SportSweeps',
        description: 'Enter your access code to join a fantasy golf pool with your society or group.',
      };
    }
    const societyName = room.society?.name || 'a group';
    const tournamentName = room.tournament_name || 'a tournament';
    return {
      title: `Join ${societyName}'s ${tournamentName} pool | SportSweeps`,
      description: `${societyName} is running a fantasy golf pool for ${tournamentName}. Pick your team before the tee-off.`,
      image: room.society?.logo || undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    };
  }, [room]);
  useSEO(seo);
  const [accessCode, setAccessCode] = useState(urlCode || '');
  const [replaced, setReplaced] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [checking, setChecking] = useState(!!urlCode);

  // Auth form state
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);

  // Step tracking: 'code' -> 'auth' -> 'enter'
  function getStep() {
    if (!validated) return 'code';
    if (!isAuthenticated) return 'auth';
    if (room) return 'enter';
    return 'code';
  }
  const step = getStep();

  const validateCode = useCallback(async (code) => {
    if (!code || code.length < 4) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/public/room/${code}`);
      setRoom({ ...data, sport: 'golf' });
      setValidated(true);
    } catch {
      // Fall back to LMS lookup — football pools live on a separate collection.
      try {
        const { data } = await api.get(`/public/lms/room/${code}`);
        setReplaced(null);
        // The URL may have carried a long join_token — show the real code.
        setAccessCode(data.access_code);
        setRoom({
          sport: 'football',
          access_code: data.access_code,
          tournament_name: data.pool_nickname || 'Premier League LMS',
          pool_nickname: data.pool_nickname,
          society: { name: data.society_name || '' },
          society_name: data.society_name,
          entry_fee_enabled: data.entry_fee_enabled,
          entry_fee_amount: data.entry_fee_amount,
          entry_fee_currency: data.entry_fee_currency,
        });
        setValidated(true);
      } catch (lmsErr) {
        // Admin rotated the code: the old link is dead on purpose, so stop
        // here with a friendly explanation instead of "invalid code".
        const detail = lmsErr.response?.status === 410 ? lmsErr.response?.data?.detail : null;
        if (detail?.code === 'link_replaced') {
          setReplaced(detail);
          setAccessCode('');
          setValidated(false);
          setLoading(false);
          setChecking(false);
          return;
        }
        // Final fallback — racing pools.
        try {
          const { data } = await api.get(`/public/racing/room/${code}`);
          setRoom({
            sport: 'horse-racing',
            access_code: data.access_code,
            tournament_name: data.pool_nickname || data.meeting_name,
            pool_nickname: data.pool_nickname,
            society: { name: data.society_name || '' },
            society_name: data.society_name,
            entry_fee_enabled: data.entry_fee_enabled,
            entry_fee_amount: data.entry_fee_amount,
            entry_fee_currency: data.entry_fee_currency,
          });
          setValidated(true);
        } catch {
          toast.error('Invalid access code. Check with your group admin.');
          setValidated(false);
        }
      }
    } finally {
      setLoading(false);
      setChecking(false);
    }
  }, [setRoom]);

  // Track the last code we validated so urlCode changes (player bouncing
  // between pools from the dashboard) re-run validation and we don't render
  // the previous pool's room data briefly while the new fetch is in-flight.
  const lastValidatedCodeRef = useRef(null);
  useEffect(() => {
    if (!urlCode) return;
    if (lastValidatedCodeRef.current === urlCode) return;
    lastValidatedCodeRef.current = urlCode;
    setValidated(false);
    setChecking(true);
    validateCode(urlCode);
  }, [urlCode, validateCode]);

  // Pre-fill display name from player account
  useEffect(() => {
    if (isAuthenticated && player && !displayName) {
      setDisplayName(player.display_name || player.name || '');
    }
  }, [isAuthenticated, player, displayName]);

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Email and password required'); return; }
    if (authMode === 'signup') {
      if (!firstName.trim()) { toast.error('First name is required'); return; }
      if (!lastName.trim()) { toast.error('Last name is required'); return; }
    }

    setAuthSubmitting(true);
    try {
      if (authMode === 'signup') {
        const result = await signup(email, password, firstName.trim(), lastName.trim(), '');
        setDisplayName(result.player.display_name || result.player.name);
        toast.success('Account created!');
      } else {
        const result = await login(email, password, '');
        setDisplayName(result.player.display_name || result.player.name);
        toast.success('Welcome back!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    } finally { setAuthSubmitting(false); }
  };

  const handleEnterRoom = () => {
    if (!displayName.trim()) { toast.error('Enter your name to continue'); return; }
    const code = accessCode || urlCode;
    if (!code) { toast.error('No access code. Please enter one first.'); return; }
    savePlayerName(displayName.trim());
    if (room?.sport === 'football') {
      navigate(`/joinpool/${code}/lms`);
    } else if (room?.sport === 'horse-racing') {
      navigate(`/joinpool/${code}/racing`);
    } else {
      navigate(`/joinpool/${code}/picks`);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    const code = accessCode.trim().toUpperCase();
    if (!code) { toast.error('Enter an access code'); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/public/room/${code}`);
      const token = data.join_token || data.access_code || code;
      navigate(`/joinpool/${token}`);
    } catch {
      // Try LMS — football pools have a different endpoint and route.
      try {
        const { data } = await api.get(`/public/lms/room/${code}`);
        navigate(`/joinpool/${data.access_code || code}`);
      } catch {
        // Try racing as a final fallback.
        try {
          const { data } = await api.get(`/public/racing/room/${code}`);
          navigate(`/joinpool/${data.access_code || code}`);
        } catch {
          toast.error('Invalid access code. Check with your group admin.');
        }
      }
    } finally { setLoading(false); }
  };

  if (checking || authLoading) {
    return (
      <PlayerShell className="flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </PlayerShell>
    );
  }

  return (
    <PlayerShell data-testid="gatekeeper-page">
      {/* Reserved for future top-right actions */}

      {/* Background hero */}
      <div className="absolute inset-0 overflow-hidden">
        <picture>
          <source media="(max-width: 767px)" type="image/webp" srcSet="/hero/hero-bg-mobile.webp" />
          <source media="(max-width: 767px)" srcSet="/hero/hero-bg-mobile.png" />
          <source type="image/webp" srcSet="/hero/hero-bg-desktop.webp" />
          <img
            src="/hero/hero-bg-desktop.png"
            alt=""
            decoding="async"
            className="w-full h-full object-cover opacity-[0.08]"
          />
        </picture>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, rgba(11,15,20,0) 0%, rgba(11,15,20,0) 55%, hsl(var(--background) / 0.85) 88%, hsl(var(--background)) 100%)'
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-start pt-6 sm:pt-[12vh] px-4 pb-20">
        <motion.div
          {...ANIM_FADE_UP}
          transition={TRANSITION_MAIN}
          className="w-full max-w-sm"
        >
          {/* Logo */}
          <div className="mb-5 sm:mb-8 text-center">
            <img src="/brand/icon-logo.png" alt="SportSweeps" className="w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4 object-contain" />
            <h1 className="font-heading text-2xl sm:text-4xl font-bold text-foreground tracking-tight">
              SportSweeps
            </h1>
            <p className="text-muted-foreground text-sm mt-2">
              {step === 'code' && 'Enter your access code to join the pool'}
              {step === 'auth' && 'Sign in or create an account to continue'}
              {step === 'enter' && 'Confirm your name and enter the room'}
            </p>
          </div>

          {replaced && step === 'code' && (
            <div
              className="mb-4 rounded-xl border border-amber-300/60 bg-amber-50 dark:bg-amber-900/15 px-4 py-3 text-left"
              data-testid="gatekeeper-link-replaced"
            >
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">This link has been replaced</p>
              <p className="text-xs text-amber-700/90 dark:text-amber-200/80 mt-1">
                {replaced.society_name ? `${replaced.society_name} has` : 'Your admin has'} issued a new code for{' '}
                <span className="font-medium">{replaced.pool_nickname || 'this pool'}</span>. Ask your admin for it
                and enter it below.
              </p>
            </div>
          )}

          <AnimatePresence mode="wait">
            {step === 'code' && (
              <CodeStep
                accessCode={accessCode} setAccessCode={setAccessCode} loading={loading}
                onSubmit={handleCodeSubmit} isAuthenticated={isAuthenticated}
                navigate={navigate} setValidated={setValidated}
              />
            )}

            {step === 'auth' && (
              <AuthStep
                room={room} email={email} setEmail={setEmail} password={password} setPassword={setPassword}
                firstName={firstName} setFirstName={setFirstName} lastName={lastName} setLastName={setLastName}
                authMode={authMode} setAuthMode={setAuthMode} authSubmitting={authSubmitting}
                onSubmit={handleAuthSubmit} urlCode={urlCode} setValidated={setValidated} navigate={navigate}
              />
            )}

            {step === 'enter' && (
              <EnterStep
                room={room} displayName={displayName} setDisplayName={setDisplayName}
                onEnter={handleEnterRoom}
              />
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </PlayerShell>
  );
}
